import React from "react";
import { Tooltip } from "antd";
import { ChevronRight } from "lucide-react";
import { useDashTheme } from "../../hooks/useDashTheme";
import { SectionCard } from "../primitives";
import { InfoDot } from "./InfoDot";
import { useReveal, DeltaChip, Spark, Bullet, fmtMins, fmtH, Shim,
  AreaSpark, SplitBar, IdleByDay, Waffle, RankStubs, GaugeArc } from "./micro";
import { siSwagger, siGoogledocs } from "simple-icons";
import { toolGlyphIdFor, ToolGlyph } from "./toolIcons";

function domainFrom(text) {
  const m = String(text || "").match(/(?:https?:\/\/)?(?:www\.)?([a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9-]+)+)/i);
  if (!m) return null;
  const d = m[1].toLowerCase().replace(/\/.*$/, "");
  if (!/\.[a-z]{2,}$/i.test(d)) return null;
  if (d.split(".").some((p) => p.length < 2)) return null;
  return d;
}

function faviconDomainFor(name) {
  const n = String(name || "").trim();
  if (/seller\s*7|seller7/i.test(n) && !/\./.test(n)) return "seller7.ai";
  return domainFrom(n);
}

function officialMark(name) {
  const n = String(name || "").toLowerCase();
  if (n.includes("swagger")) return siSwagger;
  if (n.includes("google doc")) return siGoogledocs;
  return null;
}

/**
 * The redesigned Classic panels (approved mock v5), fed by
 * useClassicOverview. Every figure a panel shows exists in the payload; every
 * clickable thing resolves to a REAL destination (drawer subject or page) —
 * a row that cannot lead anywhere is rendered unclickable, not dead.
 *
 * DETAIL panels (ToolFlow, FocusCard, the switches KPI) render ONLY when
 * `detail.coverage.covered > 0`, and always carry the neutral coverage line.
 * Collector names never appear — that distinction is internal.
 */

const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
// Organic-looking bar heights (fraction of track) for the RhythmChart skeleton —
// a repeating pattern so columns read as varied bars, not a uniform block.
const BAR_SKELETON = [0.55, 0.82, 0.4, 0.68, 0.9, 0.5, 0.35];

/* ═══ Lede: headline + story beats + fact row ═══════════════════════════ */
export function LedeCard({ headline, story, overview, loading, onDrill, onTeam }) {
  const t = useDashTheme();
  const ref = useReveal();
  const p = overview?.people;

  return (
    <div ref={ref} style={{
      background: t.c.surface, borderRadius: 10, padding: "18px 20px",
      boxShadow: `0 0 0 1px ${t.c.border}, 0 1px 3px ${t.c.shadowColor}`,
      display: "flex", flexDirection: "column", minHeight: 0,
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: t.c.textTertiary }}>
        The period, in one line
      </div>

      {loading ? (
        <Shim w="72%" h={40} style={{ marginTop: 12 }} />
      ) : headline ? (
        // Composed in code from payload figures — see composeHeadline. The only
        // markup it can contain is <b>/<em> that composeHeadline itself emits.
        <p style={{ fontSize: 19, lineHeight: 1.45, fontWeight: 500, letterSpacing: "-0.25px", margin: "10px 0 0", color: t.c.text }}
          dangerouslySetInnerHTML={{ __html: headline.replace(/<em>/g, `<em style="font-style:normal;color:${t.status.negative};font-weight:600">`) }} />
      ) : (
        <p style={{ fontSize: 15, margin: "10px 0 0", color: t.c.textTertiary }}>No tracked time in this window.</p>
      )}

      {/* Side-by-side tiles, not stacked rows — a full-width card with a
          narrow left column of beats read as "empty" (user feedback). */}
      <div style={{ marginTop: 14, marginBottom: 14, flex: 1, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10 }}>
        {loading
          ? Array.from({ length: 3 }, (_, i) => <Shim key={i} h={118} r={9} />)
          : (story || []).map((b, i) => (
            <Beat key={i} t={t} n={i + 1} beat={b} onDrill={onDrill} />
          ))}
      </div>

      {loading ? (
        <div style={{ marginTop: "auto", paddingTop: 14, borderTop: `1px solid ${t.c.border}` }}>
          <Shim w={260} h={13} />
        </div>
      ) : p && (
        <div style={{
          marginTop: "auto", paddingTop: 14, borderTop: `1px solid ${t.c.border}`,
          display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, color: t.c.textTertiary,
        }}>
          <span><b style={{ color: t.c.text }}>{p.tracked}/{p.total}</b> tracked time</span>
          {overview.totals.trackedMinutes > 0 && (
            <>
              <span style={{ opacity: 0.6 }}>·</span>
              <span>idle <b style={{ color: t.c.text }}>{Math.round((overview.totals.idleMinutes / overview.totals.trackedMinutes) * 100)}%</b></span>
            </>
          )}
          {p.silent.length > 0 && (
            <>
              <span style={{ opacity: 0.6 }}>·</span>
              <button onClick={onTeam} style={{
                border: "none", background: "none", padding: "2px 6px", borderRadius: 6,
                color: t.status.negative, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: t.font.family,
              }}>
                {p.silent.length} silent this period ›
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Beat({ t, n, beat, onDrill }) {
  const clickable = !!beat.drill;
  const body = (
    <>
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          width: 22, height: 22, borderRadius: 999, background: t.c.primarySoft, color: t.c.primaryHover,
          fontSize: 11, fontWeight: 700, display: "grid", placeItems: "center", flexShrink: 0,
        }}>{n}</span>
        <span style={{ flex: 1 }} />
        {clickable && <ChevronRight size={15} style={{ color: t.c.textTertiary, flexShrink: 0 }} />}
      </span>
      <span style={{ display: "block", fontSize: 12.5, lineHeight: 1.55, color: t.c.textSecondary, marginTop: 9 }}
        dangerouslySetInnerHTML={{ __html: beat.html.replace(/<b>/g, `<b style="color:${t.c.text}">`) }} />
      {/* viz pinned to the tile bottom, full width — the tile IS the chart */}
      <span style={{ display: "block", marginTop: "auto", paddingTop: 12 }}><BeatViz t={t} beat={beat} /></span>
    </>
  );
  const style = {
    display: "flex", flexDirection: "column", minHeight: 118, minWidth: 0,
    padding: "12px 14px 14px", borderRadius: 9, border: "none",
    background: t.c.surfaceMuted, textAlign: "left", width: "100%",
    cursor: clickable ? "pointer" : "default", fontFamily: t.font.family,
    transition: "box-shadow .16s, transform .16s",
  };
  return clickable ? (
    <Tooltip title={beat.tip} mouseEnterDelay={0.3}>
      <button style={style} onClick={() => onDrill(beat.drill)}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 1px ${t.c.primaryRing}, 0 4px 12px ${t.c.shadowColor}`; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; e.currentTarget.style.transform = "none"; }}>
        {body}
      </button>
    </Tooltip>
  ) : (
    <div style={style}>{body}</div>
  );
}

function BeatViz({ t, beat }) {
  // Every variant stretches to its tile's full width — fixed pixel widths
  // are what made the old stacked layout look sparse.
  if (beat.kind === "share") {
    return (
      <span style={{ display: "flex", height: 8, borderRadius: 999, overflow: "hidden", width: "100%" }}>
        {beat.segments.map((s, i) => (
          <i key={i} className="tgcl-widen" style={{
            display: "block", height: "100%", width: `${s.pct}%`,
            background: s.hot ? t.status.negative : (i === 0 ? t.c.primary : t.c.borderStrong),
            animationDelay: `${0.3 + i * 0.06}s`,
          }} />
        ))}
      </span>
    );
  }
  if (beat.kind === "compare") {
    const max = Math.max(...beat.rows.map((r) => r.minutes), 1);
    return (
      <span style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%" }}>
        {beat.rows.map((r, i) => (
          <span key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10.5, color: t.c.textTertiary }}>
            <em style={{ fontStyle: "normal", width: 92, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</em>
            <span style={{ flex: 1, height: 7, borderRadius: 999, background: t.c.borderStrong, overflow: "hidden", opacity: 0.9 }}>
              <i className="tgcl-widen" style={{
                display: "block", height: "100%", borderRadius: 999, width: `${Math.max(4, (r.minutes / max) * 100)}%`,
                background: r.hot ? t.status.negative : t.c.primary, animationDelay: `${0.3 + i * 0.1}s`,
              }} />
            </span>
            <b style={{ fontVariantNumeric: "tabular-nums", color: t.c.textSecondary, flexShrink: 0 }}>{fmtH(r.minutes)}</b>
          </span>
        ))}
      </span>
    );
  }
  if (beat.kind === "curve") {
    const c = beat.curve || [];
    const max = Math.max(...c, 1);
    const pts = c.map((v, i) => `${(i * 120 / 23).toFixed(1)},${(25 - 20 * (v / max)).toFixed(1)}`);
    return (
      <svg viewBox="0 0 122 30" preserveAspectRatio="none" style={{ width: "100%", height: 42, display: "block" }}>
        <polygon points={`0,30 ${pts.join(" ")} 120,30`} fill={t.status.positive} opacity="0.10" />
        <polyline points={pts.join(" ")} fill="none" stroke={t.status.positive} strokeWidth="1.8" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        {[beat.peaks.first, beat.peaks.second].filter((h) => h != null).map((h) => (
          <circle key={h} cx={(h * 120 / 23).toFixed(1)} cy={(25 - 20 * (c[h] / max)).toFixed(1)} r="2.6" fill={t.status.positive} />
        ))}
      </svg>
    );
  }
  return null;
}

/* ═══ KPI grid — one idiom per metric ═══════════════════════════════════ */
export function KpiGrid({ overview, kpi, goals, loading, onTeam }) {
  const t = useDashTheme();
  const ref = useReveal();
  const tot = overview?.totals;
  const detail = overview?.detail;
  const series = overview?.daySeries || [];
  const spark = series.map((d) => d.activeMinutes + d.idleMinutes);
  // prevTotalMinutes already rides on daySeries, so the ghost line is real
  // data rather than a second fetch.
  const prevSpark = series.map((d) => d.prevTotalMinutes);

  const delta = tot && tot.prevTrackedMinutes > 0
    ? Math.round(((tot.trackedMinutes - tot.prevTrackedMinutes) / tot.prevTrackedMinutes) * 100)
    : null;
  const activePct = tot && tot.trackedMinutes > 0 ? Math.round((tot.activeMinutes / tot.trackedMinutes) * 100) : 0;
  const swDelta = detail?.switches && detail.switches.prevTotal > 0
    ? Math.round(((detail.switches.total - detail.switches.prevTotal) / detail.switches.prevTotal) * 100)
    : null;

  const cells = [];
  if (tot) {
    cells.push({
      k: "Tracked", info: "hours", v: fmtH(tot.trackedMinutes),
      chip: delta != null ? { dir: delta >= 0 ? "up" : "down", text: `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta)}%` } : null,
      nt: tot.prevTrackedMinutes > 0 ? `vs ${fmtH(tot.prevTrackedMinutes)} last period` : "no previous period to compare",
      viz: <AreaSpark values={spark} prev={prevSpark} tip="Tracked time by day · dashed line is the previous period" />,
    });
    cells.push({
      k: "Active", info: "active", v: fmtH(tot.activeMinutes),
      chip: { dir: "flat", text: `${activePct}% active` },
      nt: `${fmtMins(tot.idleMinutes)} idle`,
      // Zero tracked time used to paint a FULL idle-coloured bar — visually
      // claiming 100% idle when there was simply nothing. Empty track instead.
      viz: <SplitBar activeMinutes={tot.activeMinutes} idleMinutes={tot.idleMinutes} />,
    });
    // 3 · Idle gets its own tile. The mock drew idle by HOUR; we do not store
    // that (weekMatrix is ACTIVE minutes per hour), so IdleByDay reads
    // daySeries[].idleMinutes instead and marks the worst day.
    if (series.length) {
      const idlePct = tot.trackedMinutes > 0 ? Math.round((tot.idleMinutes / tot.trackedMinutes) * 100) : 0;
      const worst = series.reduce((a, b) => ((b.idleMinutes || 0) > (a.idleMinutes || 0) ? b : a), series[0]);
      cells.push({
        k: "Idle", info: "idle", v: fmtH(tot.idleMinutes),
        chip: { dir: "flat", text: `${idlePct}% of tracked` },
        nt: worst ? `worst day ${worst.dateLocal} · ${fmtMins(worst.idleMinutes)}` : "no idle recorded",
        viz: <IdleByDay series={series} />,
      });
    }
  }
  if (overview?.projects?.length) {
    const ranked = overview.projects
      .filter((x) => x && x.name)
      .map((x) => ({ label: x.name, value: x.minutes }))
      .sort((a, b) => b.value - a.value);
    cells.push({
      k: "Active projects", info: "projects", v: String(ranked.length),
      chip: null,
      nt: ranked.length ? `${ranked[0].label} leads with ${fmtH(ranked[0].value)}` : "no project carried time",
      viz: <RankStubs items={ranked} color={t.c.ai} />,
    });
  }
  if (overview?.people) {
    cells.push({
      k: "People tracking", info: "presence", v: `${overview.people.tracked}/${overview.people.total}`,
      chip: overview.people.silent.length ? { dir: "down", text: `▼ ${overview.people.silent.length}` } : { dir: "up", text: "everyone" },
      nt: overview.people.silent.length ? `${overview.people.silent.length} silent — click to see who` : "everyone tracked time",
      viz: <Waffle on={overview.people.tracked} total={overview.people.total} />,
      onClick: overview.people.silent.length ? onTeam : null,
    });
  }
  if (detail?.switches) {
    cells.push({
      k: "Tool switches", info: "focus", v: detail.switches.total >= 1000 ? `${(detail.switches.total / 1000).toFixed(1)}k` : String(detail.switches.total),
      chip: swDelta != null ? { dir: swDelta > 0 ? "down" : "up", text: `${swDelta > 0 ? "▲" : "▼"} ${Math.abs(swDelta)}%` } : null,
      nt: `avg ${detail.switches.avgGapMinutes ?? "—"}m between · ${detail.coverage.covered} of ${detail.coverage.total} covered`,
      viz: <Spark values={detail.switches.perDay} tip="Switches per day" />,
    });
  }
  if (kpi) {
    cells.push({
      k: "Productivity", info: "active", v: `${kpi.utilization || 0}%`,
      chip: { dir: (kpi.utilization || 0) >= 80 ? "up" : "flat", text: (kpi.utilization || 0) >= 80 ? "on target" : "below target" },
      nt: "active share of tracked · target 80%",
      viz: <Bullet value={kpi.utilization || 0} target={80} />,
    });
  }
  if (goals && goals.totalGoalsCount > 0) {
    cells.push({
      k: "Goal score", info: "goal", v: `${Math.round(goals.teamAverageScore || 0)}%`,
      chip: Number.isFinite(goals.previousMonthChange) ? { dir: goals.previousMonthChange >= 0 ? "up" : "down", text: `${goals.previousMonthChange >= 0 ? "▲" : "▼"} ${Math.abs(Math.round(goals.previousMonthChange))}%` } : null,
      nt: `${goals.completedGoalsCount} of ${goals.totalGoalsCount} goals done`,
      viz: <GaugeArc done={goals.completedGoalsCount} total={goals.totalGoalsCount} />,
    });
  }

  return (
    <div ref={ref} style={{
      display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1, alignContent: "start",
      background: t.c.border, borderRadius: 10, overflow: "hidden",
      boxShadow: `0 0 0 1px ${t.c.border}`,
    }}>
      {loading
        ? Array.from({ length: 4 }, (_, i) => (
          <div key={i} style={{ background: t.c.surface, height: 130, padding: "15px 16px" }}>
            <Shim w={72} h={10} />
            <Shim w={96} h={26} style={{ marginTop: 10 }} />
            <Shim w="82%" h={10} style={{ marginTop: 14 }} />
          </div>
        ))
        : cells.map((c, i) => {
          const Tag = c.onClick ? "button" : "div";
          return (
            <Tag key={c.k} onClick={c.onClick || undefined} style={{
              background: t.c.surface, padding: "15px 16px 13px", border: "none", textAlign: "left",
              cursor: c.onClick ? "pointer" : "default", fontFamily: t.font.family,
            }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 11, color: t.c.textTertiary }}>{c.k}</span>
                {c.info && <InfoDot metric={c.info} />}
              </div>
              <div style={{ fontSize: 26, fontWeight: 600, letterSpacing: "-0.9px", marginTop: 7, lineHeight: 1, color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{c.v}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 8, minHeight: 18 }}>
                {c.chip && <DeltaChip dir={c.chip.dir}>{c.chip.text}</DeltaChip>}
              </div>
              <div style={{ fontSize: 10.5, color: t.c.textTertiary, marginTop: 3 }}>{c.nt}</div>
              {c.viz}
            </Tag>
          );
        })}
    </div>
  );
}

/* ═══ Working rhythm — active/idle by day + last-period ghost ═══════════ */
export function RhythmChart({ overview, loading }) {
  const t = useDashTheme();
  const ref = useReveal();
  const series = overview?.daySeries || [];
  const mx = Math.max(...series.map((d) => Math.max(d.activeMinutes + d.idleMinutes, d.prevTotalMinutes)), 60);

  return (
    <SectionCard
      title="Working rhythm"
      subtitle="Active vs idle by day · the slim bar beside each day is the previous period"
      padding={16}
      actions={<><Legend t={t} items={[[t.status.positive, "Active"], [t.status.warning, "Idle"], [t.c.borderStrong, "Prev"]]} /><InfoDot metric="rhythm" /></>}
    >
      <div ref={ref} style={{ padding: "6px 4px 2px" }}>
        {loading ? (
          // Same bar-column shape as the real chart, not a flat block — a
          // fixed height-per-column pattern so it reads as "bars", not noise.
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 150 }}>
            {Array.from({ length: series.length || 7 }, (_, i) => BAR_SKELETON[i % BAR_SKELETON.length]).map((hPct, i) => (
              <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 0 }}>
                <Shim w={22} h={11} />
                <span style={{ width: "100%", maxWidth: 44, height: 118, display: "flex", alignItems: "flex-end" }}>
                  <Shim w="100%" h={Math.round(118 * hPct)} r={5} />
                </span>
                <Shim w={20} h={9} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 150 }}>
            {series.map((d, i) => {
              const total = d.activeMinutes + d.idleMinutes;
              const h = Math.round((total / mx) * 118);
              const g = Math.round((d.prevTotalMinutes / mx) * 118);
              return (
                <Tooltip key={d.dateLocal} title={`${d.dateLocal} · Active ${fmtMins(d.activeMinutes)} · Idle ${fmtMins(d.idleMinutes)}${d.prevTotalMinutes ? ` · prev ${fmtMins(d.prevTotalMinutes)}` : ""}`}>
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: t.c.text, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                      {total ? fmtH(total) : "—"}
                    </span>
                    <span style={{ position: "relative", width: "100%", maxWidth: 44, display: "flex", alignItems: "flex-end", justifyContent: "center", height: 118 }}>
                      {g > 0 && <span style={{ position: "absolute", left: -8, bottom: 0, width: 6, height: g, borderRadius: 3, background: t.c.borderStrong, opacity: 0.55 }} />}
                      {total > 0 && (
                        <span className="tgcl-grow" style={{
                          width: "100%", height: h, display: "flex", flexDirection: "column", justifyContent: "flex-end",
                          borderRadius: "5px 5px 2px 2px", overflow: "hidden", animationDelay: `${0.2 + i * 0.05}s`,
                        }}>
                          <span style={{ height: `${(d.idleMinutes / total) * 100}%`, background: t.status.warning, opacity: 0.75 }} />
                          <span style={{ flex: 1, background: t.status.positive }} />
                        </span>
                      )}
                    </span>
                    <span style={{ fontSize: 10, color: t.c.textTertiary }}>{d.label}</span>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

/* ═══ Week matrix 7×24 ══════════════════════════════════════════════════ */
export function WeekMatrix({ overview, loading }) {
  const t = useDashTheme();
  const ref = useReveal();
  const m = overview?.weekMatrix;
  const days = overview?.range?.days || 7;
  const max = m ? Math.max(...m.flat(), 1) : 1;
  const peak = React.useMemo(() => {
    if (!m) return null;
    let best = { v: 0 };
    m.forEach((row, di) => row.forEach((v, h) => { if (v > best.v) best = { v, di, h }; }));
    return best.v > 0 ? best : null;
  }, [m]);

  return (
    <SectionCard
      title={days > 7 ? "The typical week" : "When the team actually works"}
      subtitle={`Active time by day and hour${days > 7 ? " — longer windows fold onto weekdays" : ""}`}
      padding={16}
      actions={<>{peak ? <span style={{ fontSize: 11, color: t.c.textSecondary, background: t.c.surfaceMuted, borderRadius: 6, padding: "3px 8px", whiteSpace: "nowrap" }}>Peak {WD[peak.di]} {String(peak.h).padStart(2, "0")}:00</span> : null}<InfoDot metric="matrix" /></>}
    >
      <div ref={ref}>
        {loading || !m ? (
          // Same 7×24 grid the real heatmap draws — a flat block here read as
          // "broken", not "loading".
          <>
            <div style={{ display: "grid", gridTemplateColumns: "34px repeat(24, 1fr)", gap: 3, alignItems: "center" }}>
              {WD.map((wd, di) => (
                <React.Fragment key={di}>
                  <span style={{ fontSize: 10, color: t.c.textTertiary, textAlign: "right", paddingRight: 6 }}>{wd}</span>
                  {Array.from({ length: 24 }, (_, h) => <Shim key={h} h={15} r={3} style={{ opacity: 0.5 + 0.5 * Math.random() }} />)}
                </React.Fragment>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, marginLeft: 37 }}>
              {["00", "06", "12", "18", "23"].map((h) => <Shim key={h} w={16} h={9} />)}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "34px repeat(24, 1fr)", gap: 3, alignItems: "center" }}>
              {m.map((row, di) => (
                <React.Fragment key={di}>
                  <span style={{ fontSize: 10, color: t.c.textTertiary, textAlign: "right", paddingRight: 6 }}>{WD[di]}</span>
                  {row.map((v, h) => (
                    <Tooltip key={h} title={`${WD[di]} ${String(h).padStart(2, "0")}:00 · ${fmtMins(v)} active`} mouseEnterDelay={0.05}>
                      <span style={{
                        display: "block", height: 15, borderRadius: 3,
                        background: v === 0 ? t.c.surfaceMuted : t.status.positive,
                        opacity: v === 0 ? 0.55 : 0.15 + 0.85 * (v / max),
                      }} />
                    </Tooltip>
                  ))}
                </React.Fragment>
              ))}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, marginLeft: 37, fontSize: 10, color: t.c.textTertiary }}>
              <span>00</span><span>06</span><span>12</span><span>18</span><span>23</span>
            </div>
          </>
        )}
      </div>
    </SectionCard>
  );
}

/* ═══ Tool flow (DETAIL) ════════════════════════════════════════════════ */
export function ToolFlow({ overview, loading, onOpenAppUsage, onTool }) {
  const t = useDashTheme();
  const ref = useReveal();
  const flow = overview?.detail?.flow;
  const cov = overview?.detail?.coverage;
  if (loading && flow) {
    // Range change in flight: shimmer in the same two-column shape as the
    // real flow (left = from, right = to), not one undifferentiated block.
    return (
      <div style={{ background: t.c.surface, borderRadius: 10, padding: "18px 20px", boxShadow: `0 0 0 1px ${t.c.border}, 0 1px 3px ${t.c.shadowColor}` }}>
        <Shim w={220} h={12} />
        <div style={{ display: "flex", justifyContent: "space-between", gap: 20, marginTop: 20, height: 190 }}>
          {[0, 1].map((side) => (
            <div key={side} style={{ width: 150, display: "flex", flexDirection: "column", gap: 14 }}>
              <Shim h={22 + side * 6} r={7} />
              <Shim h={44} r={7} />
              <Shim h={30} r={7} />
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (!flow || !flow.pairs?.length) return null; // DETAIL rule: absent → not rendered

  const froms = []; const tos = [];
  const push = (list, name, count) => {
    const hit = list.find((x) => x.name === name);
    if (hit) hit.count += count; else list.push({ name, count });
  };
  flow.pairs.forEach((p) => { push(froms, p.from, p.count); push(tos, p.to, p.count); });
  froms.sort((a, b) => b.count - a.count); tos.sort((a, b) => b.count - a.count);
  const L = froms.slice(0, 3); const R = tos.slice(0, 3);

  const W = 640; const colW = 150; const lx = 14; const rx = W - 14 - colW;
  const TOP = 26; const BOTTOM = 196; const GAP = 14; const MINH = 22;
  const fit = (list) => {
    const raw = list.reduce((a, n) => a + n.count, 0) || 1;
    const avail = BOTTOM - TOP - GAP * (list.length - 1);
    let y = TOP;
    return list.map((n) => { const h = Math.max(MINH, (n.count / raw) * avail); const o = { ...n, y, h }; y += h + GAP; return o; });
  };
  const Lf = fit(L); const Rf = fit(R);
  const maxPair = Math.max(...flow.pairs.map((p) => p.count), 1);

  return (
    <SectionCard
      title="How work moves between tools"
      subtitle={`Where content is copied from and pasted to · based on activity detail from ${cov.covered} of ${cov.total} people`}
      padding={16}
      actions={<><button onClick={onOpenAppUsage} style={{ border: "none", background: "none", color: t.c.primary, fontSize: 12, fontWeight: 500, cursor: "pointer", fontFamily: t.font.family }}>App usage ›</button><InfoDot metric="flow" /></>}
    >
      <div ref={ref}>
        <svg viewBox={`0 0 ${W} 200`} style={{ width: "100%", height: 200, overflow: "visible", display: "block" }}>
          <text x={lx} y={14} fontSize="9.5" letterSpacing="0.5" fill={t.c.textTertiary}>COPIED FROM</text>
          <text x={rx} y={14} fontSize="9.5" letterSpacing="0.5" fill={t.c.textTertiary}>PASTED INTO</text>
          {flow.pairs.slice(0, 5).map((p, i) => {
            const s = Lf.find((n) => n.name === p.from); const d = Rf.find((n) => n.name === p.to);
            if (!s || !d) return null;
            const sy = s.y + s.h / 2; const ty = d.y + d.h / 2; const mid = (lx + colW + rx) / 2;
            const path = `M${lx + colW} ${sy} C${mid} ${sy}, ${mid} ${ty}, ${rx} ${ty}`;
            const hot = p.count > maxPair * 0.6;
            return (
              <g key={i}>
                <path d={path} fill="none" stroke={hot ? t.c.ai : t.c.primary} strokeOpacity={hot ? 0.4 : 0.22}
                  strokeWidth={Math.max(2, (p.count / maxPair) * 14)} strokeLinecap="round" />
                <circle r={hot ? 3 : 2.2} fill={t.c.ai} opacity={hot ? 0.55 : 0.3} pointerEvents="none">
                  <animateMotion dur={`${(3.4 + i * 0.5).toFixed(1)}s`} begin={`${(i * 0.7).toFixed(1)}s`} repeatCount="indefinite" path={path} />
                </circle>
              </g>
            );
          })}
          {[[Lf, lx], [Rf, rx]].map(([list, x], side) => list.map((n) => {
            const tall = n.h >= 34;
            return (
              <g key={`${side}-${n.name}`} style={{ cursor: "pointer" }} onClick={() => (onTool ? onTool({ name: n.name, type: "application" }) : onOpenAppUsage())}>
                <rect x={x} y={n.y} width={colW} height={n.h} rx="7" fill={t.c.primary} opacity={side === 0 ? 0.82 : 0.92} />
                <text x={x + 12} y={tall ? n.y + n.h / 2 - 2 : n.y + n.h / 2 + 4} fontSize="11" fontWeight="600" fill="#fff" pointerEvents="none">{n.name}</text>
                {tall && <text x={x + 12} y={n.y + n.h / 2 + 13} fontSize="9.5" fill="#fff" fillOpacity="0.85" pointerEvents="none">{n.count.toLocaleString()} moves</text>}
              </g>
            );
          }))}
        </svg>
        <div style={{ fontSize: 11.5, color: t.c.textTertiary, lineHeight: 1.6, marginTop: 6 }}>
          Read left to right — the heaviest path is where research becomes work.
          {flow.copies > 0 && <> {flow.copies.toLocaleString()} copies · {flow.pastes.toLocaleString()} pastes in the window.</>}
        </div>
      </div>
    </SectionCard>
  );
}

/* ═══ Focus (DETAIL) ════════════════════════════════════════════════════ */
export function FocusCard({ overview }) {
  const t = useDashTheme();
  const ref = useReveal();
  const sw = overview?.detail?.switches;
  const cov = overview?.detail?.coverage;
  if (!sw || sw.focusScore == null) return null; // DETAIL rule

  const C = 2 * Math.PI * 38;
  return (
    <div ref={ref} style={{
      background: t.c.surface, borderRadius: 10, padding: "18px 20px",
      boxShadow: `0 0 0 1px ${t.c.border}, 0 1px 3px ${t.c.shadowColor}`,
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: t.c.textTertiary, marginBottom: 10 }}>Focus</div>
      <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
        <svg width="92" height="92" viewBox="0 0 92 92" style={{ flexShrink: 0 }}>
          <circle cx="46" cy="46" r="38" fill="none" stroke={t.c.surfaceMuted} strokeWidth="9" />
          <circle cx="46" cy="46" r="38" fill="none" stroke={t.c.primary} strokeWidth="9" strokeLinecap="round"
            strokeDasharray={C} strokeDashoffset={C * (1 - sw.focusScore / 100)} transform="rotate(-90 46 46)"
            style={{ transition: "stroke-dashoffset 1.2s cubic-bezier(.16,1,.3,1)" }} />
        </svg>
        <div>
          <div style={{ fontSize: 23, fontWeight: 600, letterSpacing: "-0.6px", color: t.c.text, fontVariantNumeric: "tabular-nums" }}>{sw.focusScore} / 100</div>
          <div style={{ fontSize: 11, color: t.c.textTertiary, marginTop: 4, lineHeight: 1.55 }}>
            Average <b style={{ color: t.c.text }}>{sw.avgGapMinutes} minutes</b> between tool switches — under 20 is where deep work
            stops happening. Based on activity detail from {cov.covered} of {cov.total} people.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══ Attention (deterministic) ═════════════════════════════════════════ */
export function AttentionCard({ attention, loading, onDrill }) {
  const t = useDashTheme();
  const ref = useReveal();
  if (loading && attention?.length) {
    return (
      <div style={{ background: t.c.surface, borderRadius: 10, padding: "18px 20px", boxShadow: `0 0 0 1px ${t.c.border}, 0 1px 3px ${t.c.shadowColor}` }}>
        <Shim w={150} h={12} />
        <Shim h={64} style={{ marginTop: 12 }} />
        <Shim h={64} style={{ marginTop: 8 }} />
      </div>
    );
  }
  if (!attention?.length) return null;
  const sevC = { negative: t.status.negative, warning: t.status.warning, positive: t.status.positive, info: t.status.info };

  return (
    <div ref={ref} style={{
      background: t.c.surface, borderRadius: 10, padding: "18px 20px",
      boxShadow: `0 0 0 1px ${t.c.border}, 0 1px 3px ${t.c.shadowColor}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", color: t.c.textTertiary }}>Needs your attention</span>
        <InfoDot metric="attention" />
      </div>
      {attention.map((a, i) => {
        const clickable = !!a.drill;
        const Tag = clickable ? "button" : "div";
        return (
          <Tag key={i} onClick={clickable ? () => onDrill(a.drill) : undefined} style={{
            display: "flex", gap: 10, padding: "11px 13px", borderRadius: 8, width: "100%",
            background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}`, border: "none",
            textAlign: "left", marginBottom: 8, cursor: clickable ? "pointer" : "default", fontFamily: t.font.family,
          }}>
            <i style={{ width: 3, borderRadius: 2, flexShrink: 0, background: sevC[a.sev] || t.status.info, fontStyle: "normal" }} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: "block", fontSize: 12.5, fontWeight: 600, lineHeight: 1.4, color: t.c.text }}>{a.title}</span>
              <span style={{ display: "block", fontSize: 11.5, color: t.c.textSecondary, lineHeight: 1.55, marginTop: 3 }}>{a.detail}</span>
            </span>
          </Tag>
        );
      })}
    </div>
  );
}

/* ═══ Projects: share bar + graphical rows, ONE palette ═════════════════ */
export const projectColors = (t) => [t.c.primary, t.status.positive, t.c.ai, t.status.warning, t.c.brand, t.status.info];

export function ProjectShareBar({ overview, onDrill }) {
  const t = useDashTheme();
  const projects = overview?.projects || [];
  if (projects.length < 2) return null;
  const total = projects.reduce((a, p) => a + p.minutes, 0) || 1;
  const colors = projectColors(t);

  return (
    <div style={{ display: "flex", height: 13, borderRadius: 999, overflow: "hidden", margin: "0 0 6px", boxShadow: `inset 0 0 0 1px ${t.c.border}` }}>
      {projects.slice(0, 6).map((p, i) => (
        <Tooltip key={p.projectId} title={`${p.name} · ${fmtH(p.minutes)} · ${Math.round((p.minutes / total) * 100)}% of team effort — click to open`}>
          <span
            onClick={() => onDrill({ kind: "project", id: p.projectId, name: p.name })}
            style={{ display: "block", height: "100%", width: `${Math.max(2, (p.minutes / total) * 100)}%`, background: colors[i % colors.length], opacity: 0.85, cursor: "pointer" }}
          />
        </Tooltip>
      ))}
    </div>
  );
}

/**
 * ProjectsPanel — the "Most active projects" card body, graphical.
 * The colour swatch on each row IS its segment in the share bar above — one
 * palette, so the mapping is legible at a glance. Each row: swatch · name ·
 * members · a proportional bar · delta vs previous period · time · a door.
 */
export function ProjectsPanel({ overview, membersById, onDrill }) {
  const t = useDashTheme();
  const projects = (overview?.projects || []).slice(0, 6);
  if (!projects.length) return null;
  const colors = projectColors(t);
  const max = Math.max(...projects.map((p) => p.minutes), 1);
  const total = projects.reduce((a, p) => a + p.minutes, 0) || 1;

  return (
    <div style={{ padding: "12px 16px 10px" }}>
      <ProjectShareBar overview={overview} onDrill={onDrill} />
      <div style={{ marginTop: 12 }}>
        {projects.map((p, i) => {
          const delta = p.prevMinutes >= 60 ? Math.round(((p.minutes - p.prevMinutes) / p.prevMinutes) * 100) : null;
          const members = membersById ? membersById.get(String(p.projectId)) : null;
          return (
            <button
              key={p.projectId}
              onClick={() => onDrill({ kind: "project", id: p.projectId, name: p.name })}
              style={{
                display: "grid", gridTemplateColumns: "10px minmax(0,1fr) auto auto auto", alignItems: "center",
                gap: 11, width: "100%", padding: "9px 10px", margin: "0 -10px", borderRadius: t.r.sm,
                border: "none", background: "none", textAlign: "left", cursor: "pointer", fontFamily: t.font.family,
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = t.c.surfaceMuted; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "none"; }}
            >
              <span style={{ width: 10, height: 10, borderRadius: 3, background: colors[i % colors.length], flexShrink: 0 }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 13, fontWeight: 500, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.name}
                </span>
                <span style={{ display: "block", fontSize: 11, color: t.c.textTertiary, marginTop: 2 }}>
                  {Math.round((p.minutes / total) * 100)}% of project time{members != null ? ` · ${members} member${members === 1 ? "" : "s"}` : ""}
                </span>
              </span>
              <span style={{ width: 104, height: 6, borderRadius: 999, background: t.c.surfaceMuted, overflow: "hidden", flexShrink: 0 }}>
                <span className="tgcl-widen" style={{
                  display: "block", height: "100%", borderRadius: 999,
                  width: `${Math.max(3, (p.minutes / max) * 100)}%`,
                  background: colors[i % colors.length], animationDelay: `${0.2 + i * 0.05}s`,
                }} />
              </span>
              {delta != null
                ? <DeltaChip dir={delta >= 0 ? "up" : "down"}>{delta >= 0 ? "▲" : "▼"} {Math.abs(delta)}%</DeltaChip>
                : <span />}
              <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: t.c.text, minWidth: 62, textAlign: "right" }}>
                  {fmtMins(p.minutes)}
                </span>
                <ChevronRight size={14} style={{ color: t.c.textTertiary }} />
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 10.5, color: t.c.textTertiary, marginTop: 6 }}>
        Time attributed to projects — unattributed hours stay in the dashboard totals, never redistributed.
      </div>
    </div>
  );
}

/* ═══ Tools list: recognisable at a glance ═══════════════════════════════
   We store no app icons, so identity comes from four honest layers:
     websites → the site's real favicon (graceful monogram fallback on error);
     known apps → real brand glyphs (bundled SVGs, ./toolIcons.jsx);
     known-but-unglyphed apps → brand-coloured monogram tiles (map below);
     everything else → the token accent palette, deterministic per name.
   Sub-line carries type · category · top user; spark + proportional bar make
   the row a chart, not a table cell. Rows open the ToolDrawer. */

const BRAND = [
  ["google chrome", "#4285F4"], ["chrome", "#4285F4"], ["cursor", "#111827"],
  ["claude", "#D97757"], ["msteams", "#6264A7"], ["teams", "#6264A7"],
  ["visual studio code", "#007ACC"], ["code", "#007ACC"], ["slack", "#611F69"],
  ["figma", "#F24E1E"], ["notion", "#191919"], ["whatsapp", "#25D366"],
  ["gmail", "#EA4335"], ["github", "#24292F"], ["iterm", "#2E3440"],
  ["terminal", "#2E3440"], ["safari", "#0FB5EE"], ["firefox", "#FF7139"],
  ["excel", "#217346"], ["word", "#2B579A"], ["powerpoint", "#B7472A"],
  ["outlook", "#0F6CBD"], ["zoom", "#0B5CFF"], ["postman", "#FF6C37"],
  ["seller7", "#1B8A4A"], ["seller 7", "#1B8A4A"], ["swagger", "#85EA2D"],
  ["google docs", "#4285F4"], ["google doc", "#4285F4"], ["amazon s3", "#FF9900"],
  ["compass", "#00684A"], ["mongodb", "#00684A"], ["docker", "#2496ED"],
  ["jira", "#0052CC"], ["linear", "#5E6AD2"], ["discord", "#5865F2"],
  ["opera", "#FF1B2D"], ["edge", "#0C59A4"], ["wps", "#E23E31"],
];

export function brandColorFor(name, t) {
  const n = String(name || "").toLowerCase();
  for (const [key, color] of BRAND) if (n.includes(key)) return color;
  return null; // caller falls back to the deterministic accent palette
}

/** White or near-black glyph, by tile luminance — no unreadable monograms. */
export function glyphColor(hex) {
  const c = hex.replace("#", "");
  const r = parseInt(c.slice(0, 2), 16); const g = parseInt(c.slice(2, 4), 16); const b = parseInt(c.slice(4, 6), 16);
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) > 160 ? "#1D1F21" : "#FFFFFF";
}

/* Fallback mark for apps outside the brand registry. Initials ("FZ", "DB") tell
   the reader nothing; the kind of work does. The registry covers ~50 apps, which
   is 90% of samples - this is what the remaining tail looks like. */
const CAT_GLYPH = {
  deep: <path d="M32 30 14 50l18 20M68 30l18 20-18 20M58 22 42 78" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round" strokeLinejoin="round" />,
  browse: <><circle cx="44" cy="44" r="26" fill="none" stroke="currentColor" strokeWidth="8" /><path d="M63 63 84 84" stroke="currentColor" strokeWidth="9" strokeLinecap="round" /></>,
  comms: <path d="M18 24h64a6 6 0 0 1 6 6v34a6 6 0 0 1-6 6H44L26 84V70h-8a6 6 0 0 1-6-6V30a6 6 0 0 1 6-6Z" fill="currentColor" />,
  docs: <><path d="M28 14h30l18 18v54a6 6 0 0 1-6 6H28a6 6 0 0 1-6-6V20a6 6 0 0 1 6-6Z" fill="none" stroke="currentColor" strokeWidth="8" strokeLinejoin="round" /><path d="M56 14v20h20" fill="none" stroke="currentColor" strokeWidth="8" strokeLinejoin="round" /></>,
  review: <path d="M22 50l20 20 36-40" fill="none" stroke="currentColor" strokeWidth="10" strokeLinecap="round" strokeLinejoin="round" />,
  idle: <><circle cx="50" cy="50" r="34" fill="none" stroke="currentColor" strokeWidth="8" /><path d="M50 30v22l14 10" fill="none" stroke="currentColor" strokeWidth="8" strokeLinecap="round" /></>,
  distract: <><circle cx="50" cy="50" r="34" fill="none" stroke="currentColor" strokeWidth="8" /><path d="M50 30v26M50 68v4" stroke="currentColor" strokeWidth="9" strokeLinecap="round" /></>,
};

export function ToolIcon({ name, type, cat, size = 34 }) {
  const t = useDashTheme();
  const [favBroken, setFavBroken] = React.useState(false);
  const isSite = String(type || "").toLowerCase() === "website";
  const domainish = /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(String(name || "").trim());

  const brand = brandColorFor(name, t);
  const pair = !brand && t.accentFor ? t.accentFor(String(name || "")) : null;
  const bg = brand || (pair ? pair.bg : t.c.primarySoft);
  const fg = brand ? glyphColor(brand) : (pair ? pair.fg : t.c.primary);
  const mono = String(name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

  const favDomain = (isSite && domainish ? String(name).trim() : null) || faviconDomainFor(name);
  if (favDomain && !favBroken) {
    return (
      <span title={String(name || "")} style={{
        width: size, height: size, borderRadius: 8, background: "#FFFFFF",
        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden",
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.07)",
      }}>
        <img
          src={`https://www.google.com/s2/favicons?domain=${encodeURIComponent(favDomain)}&sz=64`}
          alt="" width={Math.round(size * 0.7)} height={Math.round(size * 0.7)}
          style={{ borderRadius: 4 }}
          onError={() => setFavBroken(true)}
        />
      </span>
    );
  }
  const mark = officialMark(name);
  if (mark) {
    return (
      <span title={String(name || "")} style={{
        width: size, height: size, borderRadius: 8, background: "#FFFFFF",
        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.07)",
      }}>
        <svg viewBox="0 0 24 24" width={Math.round(size * 0.62)} height={Math.round(size * 0.62)} aria-hidden="true">
          <path d={mark.path} fill={`#${mark.hex}`} />
        </svg>
      </span>
    );
  }
  const glyphId = toolGlyphIdFor(name);
  if (glyphId) {
    return (
      <span style={{
        width: size, height: size, borderRadius: 8, background: "#FFFFFF",
        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.07)",
      }}>
        <ToolGlyph id={glyphId} size={Math.round(size * 0.72)} />
      </span>
    );
  }
  // Outside the registry: draw the kind of work, not the initials. Falls back
  // to the monogram only when the caller cannot say what kind it was.
  const catGlyph = cat ? CAT_GLYPH[cat] : null;
  return (
    <span
      title={String(name || "")}
      style={{
        width: size, height: size, borderRadius: 8, background: bg, color: fg,
        display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        fontSize: size * 0.36, fontWeight: 700, letterSpacing: 0.2,
        boxShadow: `inset 0 0 0 1px rgba(0,0,0,0.06)`,
      }}
    >
      {catGlyph
        ? <svg width={Math.round(size * 0.56)} height={Math.round(size * 0.56)} viewBox="0 0 100 100" aria-hidden="true" style={{ display: "block" }}>{catGlyph}</svg>
        : mono}
    </span>
  );
}

export function ToolsList({ d, overview, onTool, limit = 6, loading }) {
  const t = useDashTheme();
  const rows = (d.mostVisited || []).slice(0, limit);
  if (loading) return (
    <div style={{ padding: "12px 16px 14px" }}>
      <Shim h={72} r={10} />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginTop: 10 }}>
        {Array.from({ length: 4 }, (_, i) => <Shim key={i} h={72} r={9} />)}
      </div>
    </div>
  );
  if (!rows.length) return null;
  const toMins = (v) => {
    const m = String(v || "").match(/(?:(\d+)h)?\s*(?:(\d+)m)?/);
    return (parseInt(m?.[1] || 0, 10) * 60) + parseInt(m?.[2] || 0, 10);
  };
  const enrich = new Map((overview?.tools || []).map((x) => [String(x.name).toLowerCase(), x]));

  /* HIERARCHY IS THE DESIGN. The heaviest tool is usually several times the
     rest — giving it the same row as a 20h tool is what made this card read
     as "basic". #1 becomes a hero band with its week drawn under it; the rest
     become a grid of mini-cards whose bottom strip is their share of the hero. */
  const hero = rows[0];
  const rest = rows.slice(1);
  const heroMins = Math.max(toMins(hero.time), 1);
  const total = rows.reduce((a, r) => a + toMins(r.time), 0) || 1;
  const hx = enrich.get(String(hero.name).toLowerCase());
  const heroBrand = brandColorFor(hero.name, t) || t.c.primary;
  const heroSub = [hero.type === "website" ? "Website" : "App", hero.category || null, hx?.topUser ? `top ${hx.topUser}` : null]
    .filter(Boolean).join(" · ");

  let heroArea = null;
  if (hx?.sparkMinutes?.some((v) => v > 0)) {
    const sp = hx.sparkMinutes;
    const mm = Math.max(...sp, 1);
    const pts = sp.map((v, i) => `${(i * (100 / Math.max(sp.length - 1, 1))).toFixed(1)},${(34 - 26 * (v / mm)).toFixed(1)}`);
    heroArea = (
      <svg viewBox="0 0 100 36" preserveAspectRatio="none" aria-hidden
        style={{ position: "absolute", left: 0, right: 0, bottom: 0, width: "100%", height: 38, pointerEvents: "none" }}>
        <polygon points={`0,36 ${pts.join(" ")} 100,36`} fill={heroBrand} opacity="0.10" />
        <polyline points={pts.join(" ")} fill="none" stroke={heroBrand} strokeWidth="1.6" strokeLinecap="round" opacity="0.55" />
      </svg>
    );
  }

  return (
    <div style={{ padding: "12px 16px 14px" }}>
      {/* ── the hero ── */}
      <button
        onClick={() => onTool({ name: hero.name, type: hero.type, category: hero.category })}
        style={{
          position: "relative", overflow: "hidden", display: "grid",
          gridTemplateColumns: "44px minmax(0,1fr) auto", alignItems: "center", gap: 14,
          width: "100%", padding: "14px 16px 20px", borderRadius: 10,
          border: "none", background: t.c.surface, boxShadow: `0 0 0 1px ${t.c.border}`,
          textAlign: "left", cursor: "pointer", fontFamily: t.font.family,
          transition: "box-shadow .18s, transform .18s",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 1px ${t.c.primaryRing}, 0 5px 14px ${t.c.shadowColor}`; e.currentTarget.style.transform = "translateY(-1px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 0 1px ${t.c.border}`; e.currentTarget.style.transform = "none"; }}
      >
        <span style={{ position: "relative", zIndex: 1 }}><ToolIcon name={hero.name} type={hero.type} size={44} /></span>
        <span style={{ position: "relative", zIndex: 1, minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, fontWeight: 600, color: t.c.text, letterSpacing: "-0.2px" }}>{hero.name}</span>
            <span style={{
              fontSize: 10.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
              background: t.c.primarySoft, color: t.c.primaryHover,
            }}>{Math.round((heroMins / total) * 100)}% of tool time</span>
          </span>
          <span style={{ display: "block", fontSize: 11.5, color: t.c.textTertiary, marginTop: 3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{heroSub}</span>
        </span>
        <span style={{ position: "relative", zIndex: 1, display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 21, fontWeight: 600, letterSpacing: "-0.6px", fontVariantNumeric: "tabular-nums", color: t.c.text }}>{hero.time}</span>
          <ChevronRight size={16} style={{ color: t.c.textTertiary }} />
        </span>
        {heroArea}
      </button>

      {/* ── the rest, as mini-cards ── */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10, marginTop: 10 }}>
        {rest.map((r, i) => {
          const mins = toMins(r.time);
          const ex = enrich.get(String(r.name).toLowerCase());
          const brand = brandColorFor(r.name, t) || t.c.primary;
          const sub = [r.category || (r.type === "website" ? "Website" : "App"), ex?.topUser ? `top ${ex.topUser}` : null].filter(Boolean).join(" · ");
          return (
            <button
              key={`${r.name}-${i}`}
              onClick={() => onTool({ name: r.name, type: r.type, category: r.category })}
              style={{
                display: "flex", flexDirection: "column", gap: 7, padding: "11px 12px 12px",
                borderRadius: 9, border: "none", background: t.c.surface,
                boxShadow: `0 0 0 1px ${t.c.border}`, textAlign: "left", cursor: "pointer",
                fontFamily: t.font.family, transition: "box-shadow .16s, transform .16s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 1px ${t.c.primaryRing}, 0 4px 12px ${t.c.shadowColor}`; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = `0 0 0 1px ${t.c.border}`; e.currentTarget.style.transform = "none"; }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <ToolIcon name={r.name} type={r.type} size={26} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: t.c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                <span style={{ fontSize: 12.5, fontWeight: 600, fontVariantNumeric: "tabular-nums", color: t.c.text, flexShrink: 0 }}>{r.time}</span>
              </span>
              <span style={{ fontSize: 10.5, color: t.c.textTertiary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>
              {/* share of the hero — the strip makes 81h vs 21h legible at a glance */}
              <span style={{ height: 4, borderRadius: 999, background: t.c.surfaceMuted, overflow: "hidden" }}>
                <span className="tgcl-widen" style={{
                  display: "block", height: "100%", borderRadius: 999,
                  width: `${Math.max(4, (mins / heroMins) * 100)}%`,
                  background: brand, opacity: 0.85, animationDelay: `${0.2 + i * 0.05}s`,
                }} />
              </span>
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 10.5, color: t.c.textTertiary, marginTop: 8 }}>
        Strips are each tool's share of {hero.name}. Click any tool for who used it and what was done in it.
      </div>
    </div>
  );
}

function Legend({ t, items }) {
  return (
    <span style={{ display: "flex", gap: 12, fontSize: 11, color: t.c.textTertiary, whiteSpace: "nowrap" }}>
      {items.map(([c, l]) => (
        <span key={l}><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: c, marginRight: 5 }} />{l}</span>
      ))}
    </span>
  );
}
